/**
 * Suggestion row — the VISUAL content of one autocomplete option.
 *
 * Option semantics (role=option, id, aria-selected, highlight, click/keyboard)
 * are owned by `<ComboboxOption>` (the `@letsgethome/ui/combobox` primitive);
 * this renders only the icon + title/subtitle. `suggestionHref` is the
 * frontend route builder (backend returns slug + type + scope; the frontend
 * owns routing), consumed by the SearchBar's per-option `onSelect`.
 *
 * Epoch 44 — a suggestion is a SCOPE; the mode is the SURFACE's. A district,
 * estate or building row links to the listings canvas scoped to the place
 * (a building's scope is its parent estate, resolved server-side) in the
 * mode the reader is browsing — rent from the home search, whatever hub they
 * are on otherwise — and the scoped canvas reports its own emptiness. The row
 * shows no listing count and every row is a link. Until then the row printed
 * "N for rent", preferred the mode that HAD listings over the one the reader
 * was on, and sent a place with no listings to the entity's own page — three
 * behaviours the two native apps each implemented differently.
 */
import { useLocale } from '@/shared/i18n/useTranslations';
import { buildListingUrl } from '@/shared/lib/listings/url';
import type {
  SuggestionItemData,
  SuggestionItemVariants,
  SuggestionScope,
} from '@/shared/lib/types/suggestions';
import { projectLocalized } from '@letsgethome/lib/i18n';
import type { SuggestionTypeValue } from '@letsgethome/lib/types/listings';
import { routes, type ListingFilters, type ListingsMode, type Route } from '@letsgethome/routes';
import { plaintextString, slug as toSlug, type Locale } from '@letsgethome/types/branded';
import { Building2, Home, KeyRound, MapPin } from 'lucide-react';

/** The scope union as a mapped type, so the dispatch below can index it by `kind`. */
type SuggestionScopeByKind = {
  [K in SuggestionScope['kind']]: Extract<SuggestionScope, { kind: K }>;
};

/**
 * The ONE listings filter each scope means, keyed by the scope's own
 * discriminant — exhaustive by construction, like {@link HREF_BUILDERS}, and
 * each builder receives the NARROWED scope so its BRANDED id lands in the
 * matching filter key (a `DistrictId` in `estateId` is a compile error).
 *
 * It reads the SCOPE rather than the suggestion, which is the whole point of
 * the wire carrying one: a builder that closed over `item.id` was a second
 * derivation of "which listings", and that freedom is what let two mobile
 * clients filter by the wrong key entirely.
 */
const SCOPE_FILTERS: {
  [K in SuggestionScope['kind']]: (scope: SuggestionScopeByKind[K]) => ListingFilters;
} = {
  district: (scope) => ({ districtId: scope.id }),
  estate: (scope) => ({ estateId: scope.id }),
};

/** Correlated dispatch into {@link SCOPE_FILTERS} — see {@link buildHref}. */
function scopeFilters<K extends SuggestionScope['kind']>(
  scope: SuggestionScopeByKind[K] & { kind: K }
): ListingFilters {
  return SCOPE_FILTERS[scope.kind](scope);
}

/** The listings canvas, scoped to `scope`, in the surface's `mode`. */
function browseHref(mode: ListingsMode, scope: SuggestionScope): Route {
  return routes.listings.browse(mode, scopeFilters(scope));
}

/**
 * Frontend URL builders keyed by `storefront.v1.SuggestionType` variant.
 * Backend returns slug + type + scope; the frontend owns routing.
 *
 * `SuggestionType` has no `wire_aliases_gen` entry — the lowercase short name
 * IS its canonical wire form (matches `PROTO_ENUM_VALUE_SETS.SuggestionType`).
 * Keying the mapped type over `SuggestionTypeValue` makes this exhaustive by
 * construction (a new variant fails to compile until handled) and routes the
 * variant literals through object keys, which `no-raw-wire-alias-string` does
 * not flag — so no per-site opt-out is needed. Mirrors `ICON_MAP` below.
 *
 * Each builder receives the NARROWED union member
 * (`SuggestionItemVariants[K]`), so `item.scope` inside a lambda carries the
 * variant's typed scope (the correlated-record dispatch of
 * microsoft/TypeScript#47109).
 */
const HREF_BUILDERS: {
  [K in SuggestionTypeValue]: (
    item: SuggestionItemVariants[K],
    wireSlug: string,
    mode: ListingsMode
  ) => Route;
} = {
  district: (item, _slug, mode) => browseHref(mode, item.scope),
  estate: (item, _slug, mode) => browseHref(mode, item.scope),
  // A building's scope is its PARENT ESTATE (the listings search has no
  // building scope), so the row browses the estate's homes in the surface's
  // mode — never the estate's own page, which is a directory entry and not
  // what someone typing a block name is looking for.
  building: (item, _slug, mode) => browseHref(mode, item.scope),
  // A listing suggestion is an exact match on a public id, so it goes straight
  // to the listing (S-4) — there is nothing to filter down to. The URL is the
  // shared `{slug}-{publicId}` composition, not a bespoke one; the detail page
  // binds its price slot from the listing's own offered modes.
  listing: (item, wireSlug) => buildListingUrl(toSlug(wireSlug), item.publicId),
};

/**
 * Correlated dispatch into {@link HREF_BUILDERS}: because both the builders map
 * and this signature are indexed by the SAME `SuggestionItemVariants[K]`, TS
 * proves `HREF_BUILDERS[item.type]` accepts exactly this `item` — no cast, no
 * union-signature intersection problem. The lookup is a known-key access
 * (total + always defined); exhaustiveness stays compile-time.
 */
function buildHref<K extends SuggestionTypeValue>(
  item: SuggestionItemVariants[K] & { type: K },
  wireSlug: string,
  mode: ListingsMode
): Route {
  return HREF_BUILDERS[item.type](item, wireSlug, mode);
}

/**
 * Build the frontend URL for a suggestion. `mode` is the browse mode of the
 * SURFACE the reader is on — REQUIRED, never defaulted here: the home search
 * passes `HOME_SEARCH_DEFAULT_MODE` (the one declared default) and the
 * listings pages pass their current mode, so a selection STAYS on rent/buy.
 */
export function suggestionHref(
  item: SuggestionItemData,
  locale: Locale,
  mode: ListingsMode
): Route {
  const slug = projectLocalized(item.localizedSlug, locale);
  return buildHref(item, slug, mode);
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  district: MapPin,
  estate: Home,
  building: Building2,
  listing: KeyRound,
};

/** The visual content of one suggestion (no option semantics — see module doc). */
export function SuggestionRow({ item }: { item: SuggestionItemData }) {
  const locale = useLocale();
  const Icon = ICON_MAP[item.type] ?? MapPin;

  const title = projectLocalized(item.title, locale);
  const subtitle = projectLocalized(item.subtitle, locale);

  return (
    <>
      <Icon className="size-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{plaintextString(title)}</div>
        {subtitle && (
          <div className="truncate text-sm text-muted-foreground">{plaintextString(subtitle)}</div>
        )}
      </div>
    </>
  );
}
